import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, Upload, Video as VideoIcon, Wand2, CheckCircle2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL)}/api`;

const UNIT_OPTIONS = [
  { value: 'u', label: 'unité' },
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'forfait', label: 'forfait' },
  { value: 'h', label: 'heure' },
  { value: 'km', label: 'km' },
  { value: 'cm', label: 'cm' },
  { value: 'kg', label: 'kg' },
  { value: 'm3', label: 'm³' },
];

/**
 * Dialog d'import de lignes BPU depuis un enregistrement vidéo.
 *  - upload (.mp4/.mov/.webm) → Gemini 3 Pro → retourne les lignes détectées
 *  - aperçu éditable (checkbox + inputs) → validation → merge fuzzy côté backend
 */
export const VideoImportDialog = ({ open, onOpenChange, onImported }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [progress, setProgress] = useState(null); // {phase: 'upload'|'analyze', done?, total?}
  const [lines, setLines] = useState([]); // {checked, corps_metier, libelle, unite, prix, notes}
  const [filter, setFilter] = useState('');

  const reset = () => { setFile(null); setLines([]); setFilter(''); setProgress(null); };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // Poll job status until done/error
  const pollJob = async (jobId) => {
    const maxAttempts = 120; // 120 x 3s = 6 min max
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const r = await axios.get(`${API}/bordereau/import-video/${jobId}`, { withCredentials: true });
        const st = r.data?.status;
        if (st === 'done') return r.data;
        if (st === 'error') throw new Error(r.data?.error || 'Analyse échouée');
      } catch (err) {
        if (i > 3 && err.response?.status === 404) throw new Error('Job introuvable');
        if (err.message && err.message !== 'Network Error') throw err;
        // Network blip : retry
      }
    }
    throw new Error("Délai dépassé (6 min) — essayez avec une vidéo plus courte");
  };

  // Chunked upload : bypasses the 60 s proxy timeout for big videos.
  const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB per chunk
  const uploadInChunks = async (f) => {
    const totalChunks = Math.ceil(f.size / CHUNK_SIZE);
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(f.size, start + CHUNK_SIZE);
      const blob = f.slice(start, end);
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('filename', f.name);
      fd.append('chunk', blob, `chunk_${i}`);
      await axios.post(`${API}/bordereau/import-video/chunk`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 55000,
      });
      setProgress({ phase: 'upload', done: i + 1, total: totalChunks });
    }
    // Finalize
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    const res = await axios.post(`${API}/bordereau/import-video/finalize`, fd, {
      withCredentials: true,
      timeout: 55000,
    });
    return res.data?.job_id;
  };

  const startAnalysis = async () => {
    if (!file) { toast.error('Sélectionnez un fichier'); return; }
    setAnalyzing(true);
    setLines([]);

    // Détection du type pour choisir la bonne route
    const isVideo = (file.type || '').startsWith('video/') || /\.(mp4|mov|webm|m4v|qt)$/i.test(file.name);
    const isPdf   = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    const isImage = (file.type || '').startsWith('image/') || /\.(png|jpe?g|webp|heic|heif)$/i.test(file.name);

    try {
      let jobId;
      if (isVideo && file.size > 8 * 1024 * 1024) {
        // Vidéo > 8 MB : chunked upload pour bypass timeout proxy
        setProgress({ phase: 'upload', done: 0, total: Math.ceil(file.size / CHUNK_SIZE) });
        jobId = await uploadInChunks(file);
      } else if (isVideo || isPdf || isImage) {
        // PDF, image ou petite vidéo : upload direct via /import-file (single shot)
        setProgress({ phase: 'upload', done: 0, total: 1 });
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/bordereau/import-file`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        jobId = r.data?.job_id;
        setProgress({ phase: 'upload', done: 1, total: 1 });
      } else {
        toast.error('Format non supporté. Utilise PDF, image (PNG/JPG/WEBP) ou vidéo (MP4/MOV/WEBM).');
        return;
      }
      if (!jobId) throw new Error('job_id manquant');
      setProgress({ phase: 'analyze' });
      const sourceLabel = isPdf ? 'PDF' : isImage ? 'image' : 'vidéo';
      toast.message('Analyse IA lancée', { description: `Gemini lit ton ${sourceLabel} — 10 s à 3 min selon la taille.` });

      // 2) poll until done
      const jobData = await pollJob(jobId);
      const detected = (jobData?.lines || []).map((l) => ({ ...l, checked: true }));
      setLines(detected);
      if (detected.length === 0) {
        toast.warning('Aucune ligne détectée — essaie avec une meilleure résolution / un cadrage plus serré.');
      } else {
        toast.success(`${detected.length} ligne${detected.length > 1 ? 's' : ''} détectée${detected.length > 1 ? 's' : ''}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur analyse';
      toast.error(String(msg).slice(0, 160));
    } finally {
      setAnalyzing(false);
      setProgress(null);
    }
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => {
      const n = [...prev];
      n[idx] = { ...n[idx], ...patch };
      return n;
    });
  };

  const removeLine = (idx) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return lines.map((l, idx) => ({ ...l, _idx: idx }));
    return lines
      .map((l, idx) => ({ ...l, _idx: idx }))
      .filter((l) => (l.libelle + ' ' + l.corps_metier).toLowerCase().includes(f));
  }, [lines, filter]);

  const checkedCount = lines.filter((l) => l.checked).length;
  const totalCount = lines.length;
  const allSelected = totalCount > 0 && checkedCount === totalCount;

  const toggleAll = () => {
    const target = !allSelected;
    setLines((prev) => prev.map((l) => ({ ...l, checked: target })));
  };

  const doImport = async (mode) => {
    const selected = lines.filter((l) => l.checked && l.libelle && l.prix > 0);
    if (selected.length === 0) { toast.error('Aucune ligne sélectionnée'); return; }
    setMerging(true);
    try {
      const payload = {
        lines: selected.map((l) => ({
          corps_metier: l.corps_metier || 'Divers',
          libelle: l.libelle,
          unite: l.unite || 'u',
          prix: Number(l.prix) || 0,
          notes: l.notes || '',
        })),
      };
      const endpoint = mode === 'add' ? 'import-add' : 'import-merge';
      const res = await axios.post(`${API}/bordereau/${endpoint}`, payload, { withCredentials: true });
      toast.success(res.data?.message || 'Import terminé');
      onImported?.();
      handleClose(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur import';
      toast.error(String(msg).slice(0, 140));
    } finally {
      setMerging(false);
    }
  };
  const doMerge = () => doImport('merge');
  const doAdd = () => doImport('add');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-5xl w-[95vw] max-h-[92vh] flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-800">
          <DialogTitle className="text-white flex items-center gap-2">
            <VideoIcon className="w-5 h-5 text-emerald-400" />
            Import depuis vidéo, PDF ou image
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Charge un enregistrement d'écran de ton logiciel BTP <strong>OU</strong> un PDF de bordereau <strong>OU</strong> une photo / capture d'écran. L'IA (Gemini 3 Pro) lira les lignes et les prix MO+matériel — tu valideras avant fusion.
          </p>
        </DialogHeader>

        {/* Step 1 : Upload */}
        {lines.length === 0 && (
          <div className="flex-1 px-5 py-6 flex flex-col items-center justify-center gap-4" data-testid="bpu-import-upload">
            <div
              className="w-full max-w-lg border-2 border-dashed border-zinc-700 rounded-lg p-8 flex flex-col items-center gap-3 cursor-pointer hover:border-emerald-500/60 hover:bg-emerald-500/5 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-10 h-10 text-zinc-500" />
              <div className="text-sm text-zinc-300">
                {file ? <span className="text-emerald-400 font-medium">{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</span> : 'Clique pour choisir un fichier — vidéo (.mp4/.mov/.webm), PDF ou image (.jpg/.png/.webp)'}
              </div>
              <div className="text-xs text-zinc-600">Vidéo jusqu'à 300 MB · PDF jusqu'à 30 MB · Image jusqu'à 20 MB · analyse 10 s – 3 min</div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm,application/pdf,image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="bpu-video-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => handleClose(false)} data-testid="bpu-import-cancel">
                Annuler
              </Button>
              <Button
                disabled={!file || analyzing}
                onClick={startAnalysis}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="bpu-analyze-video"
              >
                {analyzing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours…</>
                ) : (
                  <><Wand2 className="w-4 h-4 mr-2" /> Lancer l'analyse IA</>
                )}
              </Button>
            </div>
            {analyzing && (
              <div className="text-xs text-zinc-400 text-center max-w-md space-y-2">
                {progress?.phase === 'upload' && progress.total > 0 && (
                  <>
                    <p>{progress.total === 1 ? "Envoi du fichier…" : <>Envoi par tranches : <span className="text-emerald-400 font-medium">{progress.done}/{progress.total}</span></>}</p>
                    <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
                    </div>
                  </>
                )}
                {progress?.phase === 'analyze' && (
                  <p>🧠 Gemini 3 Pro lit ton document… Cela peut prendre 10 s à 3 min. Ne ferme pas cette fenêtre.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2 : Preview */}
        {lines.length > 0 && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="px-5 py-3 border-b border-zinc-800 flex flex-wrap gap-3 items-center" data-testid="bpu-import-preview">
              <div className="text-sm text-zinc-300">
                <span className="text-emerald-400 font-medium">{checkedCount}</span> / {totalCount} lignes sélectionnées
              </div>
              <Input
                placeholder="Filtrer…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white h-8 w-52"
                data-testid="bpu-filter-input"
              />
              <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 h-8" onClick={toggleAll} data-testid="bpu-toggle-all">
                {allSelected ? 'Tout décocher' : 'Tout cocher'}
              </Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" className="border-zinc-700 text-zinc-300 h-8" onClick={reset} data-testid="bpu-restart-import">
                  Reprendre
                </Button>
                <Button
                  variant="outline"
                  className="border-amber-600/50 bg-amber-600/10 text-amber-300 hover:bg-amber-600/20 hover:text-amber-200 h-8"
                  onClick={doAdd}
                  disabled={merging || checkedCount === 0}
                  data-testid="bpu-add-confirm"
                  title="Ajoute toutes les lignes sélectionnées comme nouvelles entrées (sans fusion ni dédoublonnage)"
                >
                  {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Ajouter {checkedCount}
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"
                  onClick={doMerge}
                  disabled={merging || checkedCount === 0}
                  data-testid="bpu-merge-confirm"
                  title="Fusionne intelligemment : met à jour les prix des lignes existantes, ajoute uniquement les nouvelles"
                >
                  {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Fusionner {checkedCount}
                </Button>
              </div>
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-900 border-b border-zinc-800">
                  <tr className="text-zinc-500 uppercase tracking-wider text-[10px]">
                    <th className="p-2 text-left w-8"></th>
                    <th className="p-2 text-left">Corps de métier</th>
                    <th className="p-2 text-left">Libellé</th>
                    <th className="p-2 text-left w-24">Unité</th>
                    <th className="p-2 text-right w-24">Prix €</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l._idx} className="border-b border-zinc-800/60 hover:bg-zinc-800/30">
                      <td className="p-2">
                        <Checkbox
                          checked={l.checked}
                          onCheckedChange={(v) => updateLine(l._idx, { checked: !!v })}
                          data-testid={`bpu-row-check-${l._idx}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={l.corps_metier || ''}
                          onChange={(e) => updateLine(l._idx, { corps_metier: e.target.value })}
                          className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={l.libelle || ''}
                          onChange={(e) => updateLine(l._idx, { libelle: e.target.value })}
                          className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs"
                        />
                      </td>
                      <td className="p-2">
                        <Select value={l.unite || 'u'} onValueChange={(v) => updateLine(l._idx, { unite: v })}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={l.prix || ''}
                          onChange={(e) => updateLine(l._idx, { prix: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs text-right font-mono"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeLine(l._idx)}
                          className="text-zinc-500 hover:text-red-400"
                          title="Supprimer cette ligne de l'import"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>

            <div className="px-5 py-3 border-t border-zinc-800 text-[11px] text-zinc-500">
              💡 Fusion intelligente : les prix des libellés similaires (≥ 85 %) dans le même corps de métier seront <b>mis à jour</b>. Les nouveaux libellés seront <b>ajoutés</b>.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
